import { LitElement, css, html } from 'vendor/lit'
import { iconPlay } from 'lib/icons'
import { generateMediaQuery } from 'utils/dom'
import { externalVideoEmbedSrc, parseAspectRatio } from 'utils/_media'

class AdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    videoObjectMobile: {
      type: Object,
      attribute: 'video-object-mobile',
    },
    staticUrl: {
      type: String,
      attribute: 'static-url',
    },
    staticUrlMobile: {
      type: String,
      attribute: 'static-url-mobile',
    },
    bgSize: {
      type: String,
      attribute: 'bg-size',
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    description: {
      type: String,
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    aspectRatioMobile: {
      type: String,
      attribute: 'aspect-ratio-mobile',
    },
    posterImage: {
      type: Object,
      attribute: 'poster-image',
    },
    posterImageMobile: {
      type: Object,
      attribute: 'poster-image-mobile',
    },
    posterImageSizes: {
      type: String,
      attribute: 'poster-image-sizes',
    },
    posterImageLoading: {
      type: String,
      attribute: 'poster-image-loading',
    },
  }

  get computedAspectRatio() {
    return this.aspectRatio || !this.videoObject ? parseAspectRatio(this.aspectRatio) : this.videoObject.aspect_ratio
  }

  get computedAspectRatioMobile() {
    if (!this.videoObjectMobile && !this.aspectRatioMobile) return this.computedAspectRatio

    return this.videoObjectMobile ? this.videoObjectMobile.aspect_ratio : parseAspectRatio(this.aspectRatioMobile)
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }

  get sourceElements() {
    return [...this.renderRoot?.querySelectorAll('source')]
  }

  constructor() {
    super()
    this.primaryBreakpointTrigger = 750
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay
    this.style.setProperty('--hh-aspect-ratio', this.computedAspectRatio)
    this.style.setProperty('--hh-aspect-ratio-mobile', this.computedAspectRatioMobile)
    this.generateSources()

    this.posterImage ??= this.videoObject?.preview_image
    this.posterImageMobile ??= this.videoObjectMobile?.preview_image
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unbindSourceEvents()
  }

  bindSourceEvents() {
    if (this.boundSourceEvents?.size > 0) this.unbindSourceEvents()

    this.boundSourceEvents = []
    this.sourceElements?.forEach(source => {
      const target = window.matchMedia(source.media)
      const callback = this.onSourceBreakpoint.bind(this, source)
      this.boundSourceEvents.push({ target, callback })
      target.addEventListener('change', callback)
    })
  }

  unbindSourceEvents() {
    this.boundSourceEvents?.forEach(({ target, callback }) => {
      target.removeEventListener('change', callback)
    })
  }

  async firstUpdated() {
    // TODO: Test with vimeo source
    if (this.host === 'vimeo') {
      const { default: Vimeo } = await import('@vimeo/player')
      this.videoElement = new Vimeo.Player(this.videoElement)
    }

    this.bindSourceEvents()
  }

  handlePlayClick() {
    if (this.playing == false) {
      this.playing = true
    }
    let overlay = this.renderRoot?.querySelector('.video-overlay')
    if (overlay) overlay.style.display = 'none'
    this.videoElement.play()
  }

  handleToggleAudio() {
    this.videoElement.volume = 0.5
  }

  get isEmbed() {
    return ['vimeo', 'youtube'].includes(this.host)
  }

  generatePoster(withText = false) {
    if (!this.posterImage) return html``

    const textContent = withText
      ? html`
          ${this.title ? html`<h2>${this.title}</h2>` : html``}
          ${this.description ? html`<p>${this.description}</p>` : html``}
          <button class="video-poster-button" @click="${this.handlePlayClick}">${iconPlay || '►'}</button>
        `
      : ''

    return html`
      <div class="video-overlay">
        ${textContent}
        <adaptive-image
          data-image="${JSON.stringify(this.posterImage)}"
          data-mobile-image="${JSON.stringify(this.posterImageMobile)}"
          data-loading="${this.posterImageLoading ?? 'lazy'}"
          data-sizes="${this.posterImageSizes}"
          data-object-fit="${this.bgSize}"
          data-object-position="center"
        ></adaptive-image>
      </div>
    `
  }

  static generateSource({ src, type = 'video/mp4', width, orientation, mediaId = 'primary' }) {
    const media = generateMediaQuery({ 'max-width': width, orientation })

    if (media) {
      return html`<source src="${src}" type="${type}" media="${media}" data-id="${mediaId}" />`
    }

    return html`<source src="${src}" type="${type}" data-id="${mediaId}" />`
  }

  generateSources() {
    if (!this.videoObject && !this.staticUrl) return null

    const sources = []
    if (this.videoObject) {
      const largestSource = this.videoObject.sources.reduce(
        (acc, source) => (source.width > (acc.width ?? 0) ? source : acc),
        {}
      )

      const minimumSourceWidth = this.videoObjectMobile ? this.primaryBreakpointTrigger : 0

      const mediaId = this.videoObject.id
      const sourceArray = this.videoObject.sources.filter(
        source => (!this.loop || source.format !== 'm3u8') && source.width >= minimumSourceWidth
      )

      sourceArray.forEach(source => {
        const { url: src, mime_type: type } = source
        const width = source === largestSource ? undefined : source.width

        sources.push({ src, type, width, mediaId })
      })

      if (this.videoObjectMobile) {
        const mediaIdMobile = this.videoObjectMobile.id
        const sourceArrayMobile = this.videoObjectMobile.sources.filter(
          source => (!this.loop || source.format !== 'm3u8') && source.width <= minimumSourceWidth
        )
        const largestSourceMobile = this.videoObjectMobile.sources.reduce(
          (acc, source) => (source.width > (acc.width ?? 0) ? source : acc),
          {}
        )

        sourceArrayMobile.forEach(source => {
          const { url: src, mime_type: type } = source
          const width = source === largestSourceMobile ? minimumSourceWidth : source.width
          const orientation = 'portrait'

          sources.push({
            src,
            type,
            width,
            orientation,
            mediaId: mediaIdMobile,
          })
        })
      }
    } else {
      const src = this.staticUrl
      const type = `video/${src.split('.').pop()}`
      sources.push({ src, type, mediaId: 'primary' })

      if (this.staticUrlMobile) {
        const mobileSrc = this.staticUrlMobile
        const mobileType = `video/${mobileSrc.split('.').pop()}`
        sources.push({
          src: mobileSrc,
          type: mobileType,
          width: this.primaryBreakpointTrigger,
          mediaId: 'mobile',
        })
      }
    }

    this.sources = sources
      .sort((a, b) => (!a.width ? 1 : !b.width ? -1 : a.width - b.width))
      .map(source => AdaptiveVideo.generateSource(source))
  }

  reload() {
    this.reloadQueued = true
    const { currentTime, playState } = this.videoElement
    this.videoElement.addEventListener(
      'loadeddata',
      () => {
        this.videoElement.currentTime = currentTime.toString()
        this.videoElement.playState = playState
        this.reloadQueued = false
      },
      { once: true }
    )
    this.videoElement.load()
  }

  shouldReload(triggerSource, { matches, media }) {
    if (this.reloadQueued) return false
    if (
      (!matches && this.videoElement.currentSrc !== triggerSource.src) ||
      (matches && this.videoElement.currentSrc === triggerSource.src)
    )
      return false

    const currentSourceElement = this.sourceElements.find(s => s.src === this.videoElement.currentSrc)

    if (matches && currentSourceElement.dataset.id === triggerSource.dataset.id) return false

    return true
  }

  onSourceBreakpoint(source, event) {
    if (this.shouldReload(source, event)) {
      this.reload()
    }
  }

  renderVideo() {
    return html`
      <video
        id="video-element"
        playsinline="playsinline"
        ?autoplay="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        bg-size="${this.bgSize}"
        ${this.posterImage ? `poster="${this.posterImage}"` : html``}
        preload="none"
        muted="muted"
        aria-label="${this.description}"
      >
        ${this.sources.map(source => source)}
      </video>
    `
  }

  renderIframe() {
    return html`
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${externalVideoEmbedSrc(this.host, this.id, this.autoplay, this.controls, this.loop)}"
        title="${this.title}"
      >
      </iframe>
    `
  }

  render() {
    return html` ${this.isEmbed ? this.renderIframe() : this.renderVideo()} ${this.generatePoster(!this.autoplay)} `
  }

  static styles = css`
    :host {
      aspect-ratio: var(--hh-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }
    @media screen and (max-width: 749.9px) {
      :host {
        aspect-ratio: var(--hh-aspect-ratio-mobile, var(--hh-aspect-ratio, 16 / 9));
      }
    }
    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
    }
    #video-element[bg-size='cover'] {
      object-fit: cover;
    }
    #video-element[bg-size='contain'] {
      object-fit: contain;
    }
    .video-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    #video-element[autoplay] + .video-overlay {
      z-index: -1;
    }
    h2,
    p {
      z-index: 1;
      color: white;
    }
    p {
      width: 25%;
      text-align: center;
      margin-bottom: 3rem;
    }
    .video-overlay img,
    .video-overlay adaptive-image {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
    }
    #video-element[bg-size='cover'] + .video-overlay img {
      object-fit: cover;
    }
    #video-element[bg-size='contain'] + .video-overlay img {
      object-fit: contain;
    }
    .video-poster-button {
      align-items: center;
      background: transparent;
      border: 1px solid white;
      border-radius: 60px;
      cursor: pointer;
      width: 60px;
      height: 60px;
      padding: 1rem;
      display: flex;
      justify-content: center;
      z-index: 1;
    }
    .video-poster-button svg {
      position: relative;
      width: 30px;
      height: 30px;
      fill: white;
      stroke: white;
    }
  `
}

if (!customElements.get('hh-adaptive-video')) {
  customElements.define('hh-adaptive-video', AdaptiveVideo)
}